import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";

import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import {
  getPhysicianDisplay,
  IExamManagementNotification,
  IExamManagementNotificationDetails,
  IExamManagementNotificationLog,
} from "../../../../models/exam-management-notification-log";

const route =
  process.env.REACT_APP_EXTERNAL + "/api/v1/ExamManagementNotification";

interface IDetailsProps {
  notification: IExamManagementNotification | null;
  onClose: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : format(date, "Pp", { locale: ptBR }).replaceAll(",", "");
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <Grid item xs={12} md={6}>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      <Box component="span" fontWeight={600}>
        {label}:{" "}
      </Box>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const ExamManagementNotificationDetailsModal = ({
  notification,
  onClose,
}: IDetailsProps) => {
  const [data, setData] = useState<IExamManagementNotificationDetails>();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!notification) {
      setData(undefined);
      setHasError(false);
      return;
    }

    let active = true;
    setLoading(true);
    setHasError(false);

    GlobalAxios.get<IExamManagementNotificationDetails>(
      `${route}/${notification.id}`,
    )
      .then((response) => active && setData(response.data))
      .catch(() => active && setHasError(true))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [notification]);

  const logColumns = useMemo<GridColDef<IExamManagementNotificationLog>[]>(
    () => [
      {
        field: "created",
        headerName: "Data",
        width: 190,
        renderCell: ({ value }) => formatDate(value),
      },
      { field: "description", headerName: "Descrição", flex: 1, minWidth: 350 },
    ],
    [],
  );

  const payload = useMemo(() => {
    if (!data?.examManagementPayload) return "-";
    try {
      return JSON.stringify(JSON.parse(data.examManagementPayload), null, 2);
    } catch {
      return data.examManagementPayload;
    }
  }, [data?.examManagementPayload]);

  const physician = data ? getPhysicianDisplay(data) : "-";

  return (
    <Dialog
      open={Boolean(notification)}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Detalhes da notificação de laudo</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        )}
        {hasError && (
          <Alert severity="error">
            Não foi possível carregar os detalhes da notificação.
          </Alert>
        )}
        {!loading && !hasError && data && (
          <Stack gap={2}>
            <Grid container spacing={2}>
              <Field label="Status" value={data.status} />
              <Field
                label="Nº Atendimento"
                value={data.patientEncounterNumber}
              />
              <Field label="Médico" value={physician} />
              <Field label="Data de criação" value={formatDate(data.created)} />
              <Field
                label="Liberação do laudo"
                value={formatDate(data.reportLiberationDate)}
              />
              <Field label="Data de envio" value={formatDate(data.sentDate)} />
              <Field
                label="Controle sequencial"
                value={data.sequencialControl}
              />
            </Grid>

            {data.errorMessage && (
              <Alert severity="error">{data.errorMessage}</Alert>
            )}

            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={500}>Payload do laudo</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                  }}
                >
                  {payload}
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Typography variant="h6">Histórico</Typography>
            <TableList
              columns={logColumns}
              rows={data.logs ?? []}
              fileName={`notificacao-laudo-${data.id}`}
              showSelects={false}
              onRowSelectionModelChange={undefined}
              pageSizeOptions={[5, 10, 25]}
              hideTitle
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExamManagementNotificationDetailsModal;
